import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useConfig } from '../context/ConfigContext';

// Default FAQ items
const DEFAULT_FAQS = [
  {
    id: 1,
    question: 'How quickly will I get a response?',
    answer: 'We typically respond to all inquiries within 1-2 business days. For urgent matters, please call us during business hours.'
  },
  {
    id: 2,
    question: 'Can I schedule a call?',
    answer: "Yes! Mention your preferred date and time in your message, and we'll do our best to accommodate your schedule."
  },
  {
    id: 3,
    question: 'What if I have a payment question?',
    answer: 'For payment-related questions, please log into your dashboard first. If you still need help, select "Payment Issue" as your subject above.'
  },
  {
    id: 4,
    question: 'Can I visit a property before purchasing?',
    answer: "Absolutely! Contact us about the specific property you're interested in, and we'll provide you with location details and access information."
  }
];

function FaqSettings() {
  const navigate = useNavigate();
  const { isDemoMode } = useConfig();
  const demoDisabled = isDemoMode();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [originalFaqs, setOriginalFaqs] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchFaqs();
  }, []);

  useEffect(() => {
    // Check for changes
    setHasChanges(JSON.stringify(faqs) !== JSON.stringify(originalFaqs));
  }, [faqs, originalFaqs]);

  const fetchFaqs = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/faq', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const faqData = response.data.faqs || DEFAULT_FAQS;
      setFaqs(faqData);
      setOriginalFaqs(faqData);
      setError('');
    } catch (err) {
      console.error('Failed to fetch FAQs:', err);
      // Use defaults if no saved FAQs
      setFaqs(DEFAULT_FAQS);
      setOriginalFaqs(DEFAULT_FAQS);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      await api.post('/admin/faq',
        { faqs },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setOriginalFaqs(faqs);
      setHasChanges(false);
      setSuccess('FAQ settings saved successfully!');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to save FAQs:', err);
      setError(err.response?.data?.error || 'Failed to save FAQ settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setFaqs(originalFaqs);
    }
    navigate('/admin/settings-hub');
  };

  const handleAddFaq = () => {
    const newId = Math.max(...faqs.map(f => f.id), 0) + 1;
    const newFaq = {
      id: newId,
      question: '',
      answer: ''
    };
    setFaqs([...faqs, newFaq]);
    setEditingId(newId);
  };

  const handleDeleteFaq = (id) => {
    if (window.confirm('Are you sure you want to delete this FAQ?')) {
      setFaqs(faqs.filter(f => f.id !== id));
      if (editingId === id) {
        setEditingId(null);
      }
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newFaqs = [...faqs];
    [newFaqs[index - 1], newFaqs[index]] = [newFaqs[index], newFaqs[index - 1]];
    setFaqs(newFaqs);
  };

  const handleMoveDown = (index) => {
    if (index === faqs.length - 1) return;
    const newFaqs = [...faqs];
    [newFaqs[index], newFaqs[index + 1]] = [newFaqs[index + 1], newFaqs[index]];
    setFaqs(newFaqs);
  };

  const handleQuestionChange = (id, value) => {
    setFaqs(faqs.map(f => f.id === id ? { ...f, question: value } : f));
  };

  const handleAnswerChange = (id, value) => {
    setFaqs(faqs.map(f => f.id === id ? { ...f, answer: value } : f));
  };

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div className="admin-dashboard-header">
          <div className="admin-dashboard-header-text">
            <h1 className="admin-page-title">Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {/* Header */}
      <div className="admin-hub-header">
        <button onClick={handleCancel} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Settings
        </button>
        <h1 className="admin-page-title">FAQ Settings</h1>
        <p className="admin-hub-subtitle">Manage frequently asked questions displayed on the Contact page</p>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          backgroundColor: 'var(--color-success-bg)',
          border: '1px solid var(--color-success-border)',
          color: 'var(--color-success-text)',
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '20px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '18px' }}>✓</span> {success}
        </div>
      )}

      {/* Main Content */}
      <div className="card" style={{ padding: '25px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ marginTop: 0, marginBottom: '5px', color: 'var(--color-brand-text)' }}>
              FAQ Items ({faqs.length})
            </h2>
            <p style={{ color: 'var(--color-text-light)', margin: 0, fontSize: '14px' }}>
              Add, edit, reorder, or remove FAQ items
            </p>
          </div>
          <button
            onClick={() => !demoDisabled && handleAddFaq()}
            disabled={demoDisabled}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: demoDisabled ? 0.6 : 1, cursor: demoDisabled ? 'not-allowed' : 'pointer' }}
            title={demoDisabled ? 'Disabled in demo mode' : ''}
          >
            + Add FAQ
          </button>
        </div>

        {faqs.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: 'var(--color-surface-sunken)',
            borderRadius: '8px',
            color: 'var(--color-text-light)'
          }}>
            <p style={{ margin: 0 }}>No FAQ items yet. Click "Add FAQ" to create one.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, index) => (
              <div
                key={faq.id}
                style={{
                  border: editingId === faq.id ? '2px solid var(--color-primary)' : '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: editingId === faq.id ? '#f8f9ff' : '#fff'
                }}
              >
                {/* FAQ Header with controls */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: editingId === faq.id ? '16px' : '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                      backgroundColor: 'var(--color-primary)',
                      color: 'white',
                      borderRadius: '50%',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      {index + 1}
                    </span>
                    {editingId !== faq.id && (
                      <h4 style={{ margin: 0, color: 'var(--color-text)' }}>{faq.question || '(No question)'}</h4>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    {/* Move buttons */}
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid var(--color-border)',
                        borderRadius: '4px',
                        backgroundColor: index === 0 ? '#f5f5f5' : 'white',
                        cursor: index === 0 ? 'not-allowed' : 'pointer',
                        color: index === 0 ? '#aaa' : '#333'
                      }}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === faqs.length - 1}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid var(--color-border)',
                        borderRadius: '4px',
                        backgroundColor: index === faqs.length - 1 ? '#f5f5f5' : 'white',
                        cursor: index === faqs.length - 1 ? 'not-allowed' : 'pointer',
                        color: index === faqs.length - 1 ? '#aaa' : '#333'
                      }}
                      title="Move down"
                    >
                      ↓
                    </button>

                    {/* Edit/Done button */}
                    <button
                      onClick={() => setEditingId(editingId === faq.id ? null : faq.id)}
                      style={{
                        padding: '4px 12px',
                        border: '1px solid var(--color-border)',
                        borderRadius: '4px',
                        backgroundColor: editingId === faq.id ? 'var(--color-primary)' : 'white',
                        color: editingId === faq.id ? 'white' : '#333',
                        cursor: 'pointer'
                      }}
                    >
                      {editingId === faq.id ? 'Done' : 'Edit'}
                    </button>

                    {/* Delete button */}
                    <button
                      onClick={() => handleDeleteFaq(faq.id)}
                      style={{
                        padding: '4px 12px',
                        border: '1px solid var(--color-error-border)',
                        borderRadius: '4px',
                        backgroundColor: 'var(--color-surface)',
                        color: 'var(--color-error)',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Edit mode */}
                {editingId === faq.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                        Question
                      </label>
                      <input
                        type="text"
                        value={faq.question}
                        onChange={(e) => handleQuestionChange(faq.id, e.target.value)}
                        placeholder="Enter the question..."
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid var(--color-border)',
                          borderRadius: '6px',
                          fontSize: '16px',
                          minHeight: '44px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                        Answer
                      </label>
                      <textarea
                        value={faq.answer}
                        onChange={(e) => handleAnswerChange(faq.id, e.target.value)}
                        placeholder="Enter the answer..."
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid var(--color-border)',
                          borderRadius: '6px',
                          fontSize: '16px',
                          boxSizing: 'border-box',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  /* View mode - show answer preview */
                  <p style={{
                    margin: 0,
                    color: 'var(--color-text-light)',
                    fontSize: '14px',
                    paddingLeft: '40px'
                  }}>
                    {faq.answer || '(No answer)'}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        padding: '20px 0'
      }}>
        <button
          onClick={handleCancel}
          className="btn"
          style={{
            padding: '12px 24px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => !demoDisabled && handleSave()}
          disabled={saving || !hasChanges || demoDisabled}
          className="btn btn-primary"
          style={{
            padding: '12px 24px',
            opacity: (saving || !hasChanges || demoDisabled) ? 0.6 : 1,
            cursor: (saving || !hasChanges || demoDisabled) ? 'not-allowed' : 'pointer'
          }}
          title={demoDisabled ? 'Disabled in demo mode' : ''}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

export default FaqSettings;
